import {
  blue20,
  blue40,
  blue60,
  blue80,
  blue100,
  blue120,
  blue140,
  green60,
  green100,
  grey20,
  grey40,
  grey60,
  grey80,
  grey100,
  grey120,
  grey140,
  grey160,
  grey180,
  indigo20,
  indigo40,
  indigo60,
  indigo80,
  indigo100,
  indigo120,
  indigo140,
  lime20,
  lime40,
  lime60,
  lime80,
  lime100,
  lime120,
  lime140,
  orange20,
  orange40,
  orange60,
  orange80,
  orange100,
  orange120,
  orange140,
  orange160,
  pink100,
  purple20,
  purple40,
  purple60,
  purple80,
  purple100,
  purple120,
  purple140,
  turquoise20,
  turquoise40,
  turquoise60,
  turquoise80,
  turquoise100,
  turquoise120,
  turquoise140,
  white,
  yellow20,
  yellow40,
  yellow60,
  yellow80,
  yellow100,
  yellow120,
  yellow140,
} from "../../COREDesignSystem/Content/COREColour";

export const TECHNOLOGY_ORDER = [
  "battery",
  "hydro",
  "solar",
  "wind",
  "bioenergy",
  "coal",
  "distillate",
  "gas",
  "other",
] as const;

export type KnownTechnologyType = typeof TECHNOLOGY_ORDER[number];

export const sortByTechnologyOrder = <
  T extends { technologyType: string; technologySubType?: string | null }
>(
  items: T[]
): T[] =>
  [...items].sort((a, b) => {
    const ai = TECHNOLOGY_ORDER.indexOf(
      a.technologyType.toLowerCase() as KnownTechnologyType
    );
    const bi = TECHNOLOGY_ORDER.indexOf(
      b.technologyType.toLowerCase() as KnownTechnologyType
    );
    if (ai === -1 && bi === -1) {
      const byName = a.technologyType.localeCompare(b.technologyType);
      if (byName !== 0) return byName;
    } else if (ai === -1) {
      return 1;
    } else if (bi === -1) {
      return -1;
    } else if (ai !== bi) {
      return ai - bi;
    }
    // same technology_type (e.g. utility vs rooftop solar): keep a stable sub_type order
    return (a.technologySubType ?? "").localeCompare(b.technologySubType ?? "");
  });

export const subTypeToTypeMap: Record<string, string> = {
  utility: "solar",
  rooftop: "solar-rooftop",
  biomass: "bioenergy",
  biogas: "bioenergy-biogas",
  "black coal": "coal-black",
  "brown coal": "coal-brown",
  ccgt: "gas-ccgt",
  ocgt: "gas-ocgt",
  steam: "gas-steam",
  reciprocating: "gas-reciprocating",
  "waste coal mine": "gas-waste-coal-mine",
  distillate: "distillate",
};

const lightColourMap: Record<string, string> = {
  solar: turquoise100,
  "solar-rooftop": turquoise60,
  wind: indigo80,
  hydro: blue100,
  battery: purple100,
  gas: orange100,
  "gas-ocgt": orange120,
  "gas-ccgt": orange160,
  "gas-steam": orange100,
  "gas-reciprocating": orange80,
  "gas-waste-coal-mine": orange40,
  bioenergy: lime100,
  "bioenergy-biogas": lime60,
  coal: grey180,
  "coal-black": grey180,
  "coal-brown": grey140,
  distillate: yellow100,
  hybrid: pink100,
  portfolio: indigo100,
  other: grey40,
};

const darkColourMap: Record<string, string> = {
  ...lightColourMap,
  coal: grey140,
  "coal-black": grey140,
  "coal-brown": grey100,
};

export const getTechColour = (
  technologyType?: string | null,
  technologySubType?: string | null,
  isDark = false
): string => {
  const type = technologyType?.toLowerCase() ?? "";
  const subType = technologySubType?.toLowerCase() ?? "";
  const colourMap = isDark ? darkColourMap : lightColourMap;

  let resolved = type;
  if (subType && subTypeToTypeMap[subType]) {
    resolved = subTypeToTypeMap[subType];
  } else if (subType) {
    resolved = subType;
  }

  if (colourMap[resolved]) return colourMap[resolved];

  // Fallback: partial match, longest key first to prefer specific over generic
  // (e.g. "gas-waste-coal-mine" before "gas" or "coal")
  const match = Object.keys(colourMap)
    .sort((a, b) => b.length - a.length)
    .find((key) => resolved.includes(key));
  return match ? colourMap[match] : grey160;
};

/**
 * Shade arrays for each technology type, used by chart traces and CORETag colour props.
 * Keys are camelCase versions of the technology type + sub-type combination
 * (matching the grouping key produced by `groupUnitsByTechnology` in chartStyles.ts).
 * Each shade steps -20 from the previous, cycling up to 3 shades per technology.
 * The first shade is always the primary colour from `lightColourMap`.
 *
 * Values are ColorName strings (e.g. "blue100") rather than hex constants so that
 * consumers can look up both the hex (via allColors[name]) and the CSS class name
 * (used directly as CORETag `colour` prop) without any reverse-lookup that could
 * collide with other colors sharing the same hex value (e.g. mapOverlayColorGroup2
 * shares #1669B6 with blue100).
 */
export const technologyColourShades: Record<string, string[]> = {
  wind: ["indigo80", "indigo60", "indigo40"],
  battery: ["purple100", "purple80", "purple60"],
  solar: ["turquoise100", "turquoise80", "turquoise60"],
  hydro: ["blue100", "blue80", "blue60"],
  distillate: ["yellow100", "yellow80", "yellow60"],
  bioenergy: ["lime100", "lime80", "lime60"],
  coal: ["grey180", "grey160", "grey140"],
  gas: ["orange100", "orange80", "orange60"],
  bioenergyBiogas: ["lime60", "lime40", "lime20"],
  bioenergyBiomass: ["lime100", "lime80", "lime60"],
  coalBlackCoal: ["grey180", "grey160", "grey140"],
  coalBrownCoal: ["grey140", "grey120", "grey100"],
  gasOcgt: ["orange120", "orange100", "orange80"],
  gasCcgt: ["orange160", "orange140", "orange120"],
  gasSteam: ["orange100", "orange80", "orange60"],
  gasReciprocating: ["orange80", "orange60", "orange40"],
  gasWasteCoalMine: ["orange40", "orange20", "orange10"],
  other: ["grey40", "grey20", "grey10"],
};

/**
 * Seven tints per technology, lightest first, for charts that tell an ordered
 * sequence apart by shade rather than by hue.
 *
 * Seven is the number of asset pipeline stages, and the solar row is the tint
 * ramp spelled out in the design (Turquoise-20 → Turquoise-140). Every primary
 * technology is listed so a chart can shade any of them; hues repeat across
 * technologies because only one technology's ramp is ever on screen at a time.
 */
export const technologySequenceShades: Record<string, string[]> = {
  wind: [
    indigo20,
    indigo40,
    indigo60,
    indigo80,
    indigo100,
    indigo120,
    indigo140,
  ],
  solar: [
    turquoise20,
    turquoise40,
    turquoise60,
    turquoise80,
    turquoise100,
    turquoise120,
    turquoise140,
  ],
  battery: [
    purple20,
    purple40,
    purple60,
    purple80,
    purple100,
    purple120,
    purple140,
  ],
  hydro: [blue20, blue40, blue60, blue80, blue100, blue120, blue140],
  bioenergy: [lime20, lime40, lime60, lime80, lime100, lime120, lime140],
  coal: [grey20, grey40, grey60, grey80, grey100, grey120, grey140],
  distillate: [
    yellow20,
    yellow40,
    yellow60,
    yellow80,
    yellow100,
    yellow120,
    yellow140,
  ],
  gas: [
    orange20,
    orange40,
    orange60,
    orange80,
    orange100,
    orange120,
    orange140,
  ],
  other: [grey20, grey40, grey60, grey80, grey100, grey120, grey140],
};

/**
 * Dark-mode override for coal technologies only.
 * Coal uses lighter shades in dark mode to maintain visibility on dark backgrounds.
 */
export const darkTechnologyColourShades: Record<string, string[]> = {
  ...technologyColourShades,
  coal: ["grey140", "grey120", "grey100"],
  coalBlackCoal: ["grey140", "grey120", "grey100"],
  coalBrownCoal: ["grey100", "grey80", "grey60"],
};

const PIN_FILL_DEV: Record<string, string> = {
  solar: turquoise40,
  "solar-rooftop": turquoise40,
  wind: indigo40,
  battery: purple60,
  hydro: blue40,
  carbon: grey100,
  gas: grey100,
  "gas-ccgt": orange120,
  "gas-ocgt": orange80,
  "gas-steam": orange60,
  "gas-reciprocating": orange40,
  "gas-waste-coal-mine": orange20,
  coal: grey100,
  "coal-black": grey120,
  "coal-brown": grey80,
  distillate: yellow120,
  bioenergy: lime40,
  "bioenergy-biogas": lime40,
  hybrid: indigo60,
  portfolio: green60,
};

const PIN_FILL_OP: Record<string, string> = {
  solar: turquoise100,
  "solar-rooftop": turquoise100,
  wind: indigo80,
  battery: purple100,
  hydro: blue100,
  carbon: grey160,
  gas: grey40,
  "gas-ccgt": orange160,
  "gas-ocgt": orange120,
  "gas-steam": orange100,
  "gas-reciprocating": orange60,
  "gas-waste-coal-mine": orange20,
  coal: grey40,
  "coal-black": grey180,
  "coal-brown": grey140,
  distillate: yellow100,
  bioenergy: lime100,
  "bioenergy-biogas": lime60,
  hybrid: indigo40,
  portfolio: green100,
};

const PIN_FILL_OP_DARK: Record<string, string> = {
  ...PIN_FILL_OP,
  coal: grey80,
  "coal-black": grey140,
  "coal-brown": grey100,
};

const PIN_FILL_DEV_DARK: Record<string, string> = {
  ...PIN_FILL_DEV,
  coal: grey80,
  "coal-black": grey80,
  "coal-brown": grey60,
};

const DEFAULT_PIN_FILL_DEV = grey120;
const DEFAULT_PIN_FILL_OP = grey40;

export const getPinFillColor = (
  resolvedType: string,
  isOperational: boolean,
  isDark = false
): string => {
  if (isOperational) {
    return (
      (isDark ? PIN_FILL_OP_DARK : PIN_FILL_OP)[resolvedType] ??
      DEFAULT_PIN_FILL_OP
    );
  }
  return (
    (isDark ? PIN_FILL_DEV_DARK : PIN_FILL_DEV)[resolvedType] ??
    DEFAULT_PIN_FILL_DEV
  );
};

const PIN_ICON_OP: Record<string, string> = {
  other: grey120,
  gas: grey120,
  coal: grey120,
  "gas-waste-coal-mine": orange80,
};

const PIN_ICON_DEV: Record<string, string> = {
  "gas-reciprocating": orange80,
  "gas-waste-coal-mine": orange80,
  portfolio: indigo120,
};

export const getPinIconColor = (
  resolvedType: string,
  isOperational: boolean,
  isHover: boolean
): string => {
  if (isHover) return white;
  if (isOperational) return PIN_ICON_OP[resolvedType] ?? white;
  return PIN_ICON_DEV[resolvedType] ?? white;
};

export const getPinBorderColor = (
  resolvedType: string,
  isDevelopment: boolean
): string => {
  if (isDevelopment && resolvedType === "portfolio") return indigo120;
  return white;
};
